
/*
 * Gutter Direct - Material Toggle
 *
 * Enabled only for products tagged:
 * gutter_material_toggle
 *
 * Features:
 * - Colorbond / Zinc material toggle
 * - Native Shopify swatches and dropdown support
 * - Zinc uses Silver internally
 * - Restores previous Colorbond colour
 * - Filters Colorbond swatches by actual Shopify
 *   Material + Color variant combinations
 * - Preserves native Shopify variant handling
 *
 * No hardcoded product IDs or variant IDs.
 */

(() => {
  'use strict';

  const ROOT_SELECTOR =
    'product-info[data-gutter-material-toggle="true"]';

  const DEBUG = true;
  const PREFIX = '[GUTTER_MATERIAL]';

  const MATERIALS = ['Colorbond', 'Zinc'];
  const ZINC_COLOR = 'Silver';

  const ZINC_MESSAGE =
    'Zinc only comes in zinc, no colour to pick.';

  const productCache = new Map();

  const HIDDEN_ATTRIBUTE =
    'data-gutter-color-hidden';

  function log(...args) {
    if (DEBUG) console.log(PREFIX, ...args);
  }

  function warn(...args) {
    if (DEBUG) console.warn(PREFIX, ...args);
  }

  function error(...args) {
    console.error(PREFIX, ...args);
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  /*
   * Only work with the active picker.
   * Shopify temporarily keeps an old picker
   * during its AJAX replacement.
   */
  function getActivePicker(root) {
    return Array.from(
      root.querySelectorAll(
        '.product-order-panel variant-selects'
      )
    ).find(picker =>
      picker.isConnected &&
      picker.style.display !== 'none' &&
      picker.querySelector('.gutter-material-option')
    ) || null;
  }

  function getOptionGroup(root, name) {
    const selectors = {
      material: '.gutter-material-option',
      color: '.gutter-color-option',
      overflow: '.gutter-overflow-option'
    };

    return getActivePicker(root)
      ?.querySelector(selectors[name]) || null;
  }

  function getMaterialSelect(root) {
    return getOptionGroup(root, 'material')
      ?.querySelector('select[name^="options["]') || null;
  }

  /*
   * Support both native Shopify dropdown
   * and radio colour swatches.
   */
  function getColorControl(root) {
    const group = getOptionGroup(root, 'color');

    if (!group) return null;

    const select = group.querySelector(
      'select[name^="options["]'
    );

    if (select) {
      return {
        type: 'select',
        group,
        element: select
      };
    }

    const radios = Array.from(
      group.querySelectorAll('input[type="radio"]')
    );

    if (radios.length) {
      return {
        type: 'radio',
        group,
        radios
      };
    }

    return null;
  }

  function getColorValue(control) {
    if (!control) return null;

    if (control.type === 'select') {
      return control.element.value;
    }

    return control.radios.find(
      radio => radio.checked
    )?.value || null;
  }

  function getColorValues(control) {
    if (!control) return [];

    if (control.type === 'select') {
      return Array.from(
        control.element.options
      ).map(option => option.value);
    }

    return control.radios.map(
      radio => radio.value
    );
  }

  function hasColorValue(control, value) {
    return getColorValues(control).some(
      item => item === value
    );
  }

  /*
   * Change native Color without firing a
   * separate Shopify event.
   *
   * Native Material change fires once after
   * both option values are updated.
   */
  function setColorValue(control, value) {
    if (!hasColorValue(control, value)) {
      return false;
    }

    if (control.type === 'select') {
      const select = control.element;

      select.value = value;

      Array.from(select.options).forEach(option => {
        if (option.value === value) {
          option.setAttribute(
            'selected',
            'selected'
          );
        } else {
          option.removeAttribute('selected');
        }
      });
    } else {
      control.radios.forEach(radio => {
        const selected = radio.value === value;

        radio.checked = selected;

        if (selected) {
          radio.setAttribute('checked', 'checked');
        } else {
          radio.removeAttribute('checked');
        }
      });
    }

    const selectedLabel = control.group.querySelector(
      'legend [data-selected-value], .form__label [data-selected-value]'
    );

    if (selectedLabel) {
      selectedLabel.textContent = value;
    }

    return true;
  }

  /*
   * Load published product variant combinations
   * using Shopify's native Ajax Product API.
   *
   * Variant existence is checked independently
   * of inventory availability.
   */
  function loadProductVariants(root) {
    const picker = getActivePicker(root);
    const handle = picker?.dataset.productHandle;

    if (!handle) {
      warn('Product handle missing');
      return Promise.resolve(null);
    }

    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const shopRoot =
      window.Shopify?.routes?.root || '/';

    const base = shopRoot.endsWith('/')
      ? shopRoot
      : `${shopRoot}/`;

    const url =
      `${base}products/${encodeURIComponent(handle)}.js`;

    const request = fetch(url, {
      credentials: 'same-origin'
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(
            `Product request failed: ${response.status}`
          );
        }

        return response.json();
      })
      .then(product => {
        if (
          !product ||
          !Array.isArray(product.variants) ||
          !product.variants.length
        ) {
          warn('Variant data unavailable');
          return null;
        }

        /*
         * Shopify Ajax product data may be
         * limited for products with many variants.
         * Never hide swatches using a potentially
         * incomplete result.
         */
        if (product.variants.length >= 250) {
          warn(
            'Variant count reached Ajax API limit; ' +
            'swatch filtering skipped to avoid hiding valid colours'
          );
          return null;
        }

        log('Product variants loaded', {
          handle,
          count: product.variants.length
        });

        return product;
      })
      .catch(err => {
        warn('Unable to load product variants', err);
        return null;
      });

    productCache.set(handle, request);

    return request;
  }

  /*
   * Find native Shopify option positions.
   * Do not assume Material is option1 or
   * Color is option2.
   */
  function getOptionPosition(root, name) {
    const group = getOptionGroup(root, name);

    const position = Number(
      group?.dataset.optionPosition
    );

    if (
      !Number.isInteger(position) ||
      position < 1 ||
      position > 3
    ) {
      return null;
    }

    return position;
  }

  /*
   * Build Material -> Color existence map.
   *
   * Example:
   *
   * Colorbond -> Monument, Dune, Jasper
   * Zinc      -> Silver
   *
   * Overflow is intentionally excluded
   * from this visibility check, because a
   * colour should not disappear merely
   * because the customer selected a
   * different Overflow configuration.
   */
  function buildColorMap(root, product) {
    const materialPosition =
      getOptionPosition(root, 'material');

    const colorPosition =
      getOptionPosition(root, 'color');

    if (
      !materialPosition ||
      !colorPosition ||
      !product?.variants
    ) {
      return null;
    }

    const map = new Map();

    product.variants.forEach(variant => {
      const material = variant[
        `option${materialPosition}`
      ];

      const color = variant[
        `option${colorPosition}`
      ];

      if (!material || !color) return;

      const key = normalize(material);

      if (!map.has(key)) {
        map.set(key, new Set());
      }

      map.get(key).add(normalize(color));
    });

    return map;
  }

  function isColorSupported(map, material, color) {
    return Boolean(
      map?.get(normalize(material))
        ?.has(normalize(color))
    );
  }

  /*
   * Style only swatches/labels explicitly
   * marked by this script.
   */
  function installFilterStyle(root) {
    if (root.querySelector(
      'style[data-gutter-swatch-filter-style]'
    )) {
      return;
    }

    const style = document.createElement('style');

    style.setAttribute(
      'data-gutter-swatch-filter-style',
      ''
    );

    style.textContent = `
      product-info[data-gutter-material-toggle="true"]
      .gutter-color-option
      [${HIDDEN_ATTRIBUTE}="true"] {
        display: none !important;
      }
    `;

    root.appendChild(style);
  }

  /*
   * For each radio, hide/show the native
   * swatch label and its corresponding input.
   *
   * Do not remove DOM nodes because Shopify
   * still needs them for variant handling.
   */
  function setRadioVisibility(radio, visible) {
    if (visible) {
      radio.removeAttribute(HIDDEN_ATTRIBUTE);
    } else {
      radio.setAttribute(
        HIDDEN_ATTRIBUTE,
        'true'
      );
    }

    const labels = Array.from(radio.labels || []);

    if (!labels.length && radio.id) {
      const label = Array.from(
        radio.closest('fieldset')
          ?.querySelectorAll('label[for]') || []
      ).find(
        item => item.htmlFor === radio.id
      );

      if (label) labels.push(label);
    }

    labels.forEach(label => {
      if (visible) {
        label.removeAttribute(HIDDEN_ATTRIBUTE);
      } else {
        label.setAttribute(
          HIDDEN_ATTRIBUTE,
          'true'
        );
      }
    });
  }

  /*
   * Show only colours that have an actual
   * Shopify variant for the selected material.
   *
   * This does not filter by inventory.
   */
  function filterColorOptions(root, map) {
    if (!map) return;

    const materialSelect = getMaterialSelect(root);
    const colorControl = getColorControl(root);

    if (!materialSelect || !colorControl) return;

    const material = materialSelect.value;

    if (colorControl.type === 'radio') {
      let visibleCount = 0;

      colorControl.radios.forEach(radio => {
        const visible = isColorSupported(
          map,
          material,
          radio.value
        );

        setRadioVisibility(radio, visible);

        if (visible) visibleCount++;
      });

      log('Color swatches filtered', {
        material,
        visibleCount,
        totalCount: colorControl.radios.length
      });
    } else {
      let visibleCount = 0;

      Array.from(
        colorControl.element.options
      ).forEach(option => {
        const visible = isColorSupported(
          map,
          material,
          option.value
        );

        option.hidden = !visible;

        if (visible) visibleCount++;
      });

      log('Color dropdown filtered', {
        material,
        visibleCount
      });
    }
  }

  /*
   * Find first matching colour from native
   * Shopify options.
   */
  function findFirstSupportedColor(
    control,
    map,
    material
  ) {
    return getColorValues(control).find(
      color => isColorSupported(
        map,
        material,
        color
      )
    ) || null;
  }

  /*
   * Ensure initial Colorbond selection is
   * valid if Silver belongs only to Zinc.
   *
   * Shopify receives a native Color change
   * only when an actual correction is needed.
   */
  function correctUnsupportedColor(root, map) {
    if (!map) return;

    const materialSelect = getMaterialSelect(root);
    const colorControl = getColorControl(root);

    if (!materialSelect || !colorControl) return;

    const material = materialSelect.value;

    if (normalize(material) !== 'colorbond') {
      return;
    }

    const currentColor =
      getColorValue(colorControl);

    if (isColorSupported(
      map,
      material,
      currentColor
    )) {
      return;
    }

    const replacement =
      findFirstSupportedColor(
        colorControl,
        map,
        material
      );

    if (!replacement) {
      warn(
        'No matching Colorbond colour found'
      );
      return;
    }

    if (!setColorValue(
      colorControl,
      replacement
    )) {
      return;
    }

    root.dataset.gutterLastColorbondColor =
      replacement;

    log('Unsupported colour corrected', {
      from: currentColor,
      to: replacement
    });

    /*
     * Notify Shopify once to resolve the
     * corrected native variant selection.
     */
    const eventTarget =
      colorControl.type === 'select'
        ? colorControl.element
        : colorControl.radios.find(
            radio => radio.value === replacement
          );

    eventTarget?.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );
  }

  /*
   * Save last selected Colorbond colour.
   */
  function rememberColorbondColor(root) {
    const materialSelect = getMaterialSelect(root);
    const colorControl = getColorControl(root);

    if (!materialSelect || !colorControl) return;

    if (
      normalize(materialSelect.value) !==
      'colorbond'
    ) {
      return;
    }

    const color = getColorValue(colorControl);

    if (
      !color ||
      normalize(color) === normalize(ZINC_COLOR)
    ) {
      return;
    }

    root.dataset.gutterLastColorbondColor =
      color;

    log('Colorbond colour remembered', {
      color
    });
  }

  /*
   * Synchronize custom Material UI.
   */
  function syncUI(root) {
    const select = getMaterialSelect(root);

    if (!select) return;

    const material = select.value.trim();

    const isZinc =
      normalize(material) === 'zinc';

    root.classList.toggle(
      'gutter-zinc-active',
      isZinc
    );

    const group = getOptionGroup(
      root,
      'material'
    );

    group?.querySelectorAll(
      '.gutter-material-toggle button[data-material]'
    ).forEach(button => {
      button.setAttribute(
        'aria-pressed',
        String(
          normalize(button.dataset.material) ===
          normalize(material)
        )
      );
    });

    log('UI synchronized', {
      material,
      colorSectionVisible: !isZinc
    });
  }

  /*
   * Create material toggle.
   */
  function createToggle(root) {
    const group = getOptionGroup(
      root,
      'material'
    );

    const select = getMaterialSelect(root);

    if (!group || !select) return;

    if (!group.querySelector(
      '.gutter-material-toggle'
    )) {
      const toggle =
        document.createElement('div');

      toggle.className =
        'gutter-material-toggle';

      toggle.setAttribute('role', 'group');

      toggle.setAttribute(
        'aria-label',
        'Choose gutter material'
      );

      MATERIALS.forEach(material => {
        const exists = Array.from(
          select.options
        ).some(
          option => option.value === material
        );

        if (!exists) return;

        const button =
          document.createElement('button');

        button.type = 'button';
        button.textContent = material;
        button.dataset.material = material;

        button.setAttribute(
          'aria-pressed',
          'false'
        );

        toggle.appendChild(button);
      });

      group.appendChild(toggle);

      log('Material toggle created');
    }

    if (!group.querySelector(
      '.gutter-zinc-message'
    )) {
      const message =
        document.createElement('p');

      message.className =
        'gutter-zinc-message';

      message.textContent =
        ZINC_MESSAGE;

      group.appendChild(message);
    }
  }

  /*
   * Update swatches using product data.
   * Avoid applying results to an obsolete
   * Shopify AJAX picker.
   */
  function refreshSwatches(root) {
    const picker = getActivePicker(root);

    if (!picker) return;

    loadProductVariants(root).then(product => {
      if (!root.isConnected || !product) {
        return;
      }

      if (
        getActivePicker(root) !== picker
      ) {
        return;
      }

      const map =
        buildColorMap(root, product);

      if (!map) return;

      filterColorOptions(root, map);

      correctUnsupportedColor(root, map);
    });
  }

  function initialize(root) {
    if (!root.isConnected) return;

    if (!getActivePicker(root)) return;

    installFilterStyle(root);

    createToggle(root);
    syncUI(root);

    refreshSwatches(root);

    /*
     * Remember only after possible
     * initial colour correction is queued.
     */
    rememberColorbondColor(root);
  }

  /*
   * Material switching:
   *
   * Colorbond -> Zinc:
   *   Save Colorbond colour.
   *   Select Silver internally.
   *
   * Zinc -> Colorbond:
   *   Restore last supported colour
   *   or select the first supported
   *   Colorbond colour.
   *
   * Overflow remains untouched.
   *
   * Only one Material change event.
   */
  function onToggleClick(event, root) {
    const button = event.target.closest(
      '.gutter-material-toggle button[data-material]'
    );

    if (!button || !root.contains(button)) {
      return;
    }

    const picker = getActivePicker(root);

    if (!picker || !picker.contains(button)) {
      return;
    }

    const materialSelect =
      getMaterialSelect(root);

    const colorControl =
      getColorControl(root);

    if (!materialSelect || !colorControl) {
      error('Native option controls missing');
      return;
    }

    const nextMaterial =
      button.dataset.material;

    const previousMaterial =
      materialSelect.value;

    if (
      normalize(nextMaterial) ===
      normalize(previousMaterial)
    ) {
      return;
    }

    const materialOption = Array.from(
      materialSelect.options
    ).find(
      option => option.value === nextMaterial
    );

    if (!materialOption) {
      warn('Material option missing:', nextMaterial);
      return;
    }

    const currentColor =
      getColorValue(colorControl);

    let nextColor = currentColor;

    const isZinc =
      normalize(nextMaterial) === 'zinc';

    if (isZinc) {
      if (
        normalize(previousMaterial) === 'colorbond' &&
        currentColor &&
        normalize(currentColor) !==
          normalize(ZINC_COLOR)
      ) {
        root.dataset.gutterLastColorbondColor =
          currentColor;
      }

      nextColor = ZINC_COLOR;

      if (!hasColorValue(
        colorControl,
        nextColor
      )) {
        error(
          'Zinc requires Silver Color option'
        );
        return;
      }
    } else {
      const savedColor =
        root.dataset.gutterLastColorbondColor;

      if (
        savedColor &&
        hasColorValue(
          colorControl,
          savedColor
        )
      ) {
        nextColor = savedColor;
      }
    }

    log('Material change requested', {
      from: previousMaterial,
      to: nextMaterial,
      fromColor: currentColor,
      toColor: nextColor
    });

    if (!setColorValue(
      colorControl,
      nextColor
    )) {
      error('Failed to update Color');
      return;
    }

    materialSelect.value =
      nextMaterial;

    syncUI(root);

    materialSelect.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );

    log(
      'Native Shopify change event dispatched',
      {
        material: nextMaterial,
        color: nextColor
      }
    );

    refreshSwatches(root);
  }

  /*
   * Track native Shopify option changes.
   */
  function onNativeChange(event, root) {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const materialGroup =
      getOptionGroup(root, 'material');

    const colorGroup =
      getOptionGroup(root, 'color');

    const overflowGroup =
      getOptionGroup(root, 'overflow');

    if (
      materialGroup?.contains(target)
    ) {
      if (target.matches('select')) {
        syncUI(root);
        rememberColorbondColor(root);
        refreshSwatches(root);
      }

      return;
    }

    if (
      colorGroup?.contains(target)
    ) {
      if (
        target.matches('select') ||
        target.matches('input[type="radio"]')
      ) {
        rememberColorbondColor(root);
        refreshSwatches(root);
      }

      return;
    }

    if (
      overflowGroup?.contains(target)
    ) {
      refreshSwatches(root);
    }
  }

  /*
   * Restore functionality after Shopify
   * AJAX replaces the picker.
   */
  function observePicker(root) {
    let scheduled = false;

    const observer =
      new MutationObserver(mutations => {
        const replaced = mutations.some(
          mutation =>
            Array.from(
              mutation.addedNodes
            ).some(node => {
              if (
                node.nodeType !==
                Node.ELEMENT_NODE
              ) {
                return false;
              }

              return (
                node.matches?.('variant-selects') ||
                Boolean(
                  node.querySelector?.(
                    'variant-selects'
                  )
                )
              );
            })
        );

        if (!replaced || scheduled) {
          return;
        }

        scheduled = true;

        requestAnimationFrame(() => {
          scheduled = false;

          if (!root.isConnected) {
            observer.disconnect();
            return;
          }

          log('Shopify picker updated');

          initialize(root);
        });
      });

    observer.observe(root, {
      childList: true,
      subtree: true
    });

    log('Picker observer started');
  }

  function setup(root) {
    initialize(root);

    if (
      root.dataset.gutterToggleInitialized ===
      'true'
    ) {
      return;
    }

    root.dataset.gutterToggleInitialized =
      'true';

    root.addEventListener(
      'click',
      event => {
        onToggleClick(event, root);
      }
    );

    root.addEventListener(
      'change',
      event => {
        onNativeChange(event, root);
      }
    );

    observePicker(root);

    log('Material toggle initialized');
  }

  function run() {
    document.querySelectorAll(
      ROOT_SELECTOR
    ).forEach(root => {
      try {
        setup(root);
      } catch (err) {
        error(
          'Initialization error:',
          err
        );
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      run,
      { once: true }
    );
  } else {
    run();
  }

  document.addEventListener(
    'shopify:section:load',
    run
  );
})();
